import React, { useState } from "react";
import styles from "./ChatItem.module.css";
import clsx from "clsx";
import {
  utcToLocalDate,
  utcToLocalTime,
} from "../../../../util/ExtensionFunction";
import PromptModal from "./modals/PromptModal";
import ConfirmationDialog from "./dialogs/ConfirmationDialog";
import { NODE_DISPLAY_NAMES, NODE_INSTRUCTIONS } from "../PromptManager";
import {
  showConsoleError,
  showConsoleMessage,
} from "../../../../util/ConsoleMessage";
import {
  FAV_TAG,
  Prompt,
} from "../../../../api/responsePayload/PromptResponse";
import ToggleButton from "./buttons/ToggleButton";
import { PromptPatch, PostPrompt } from "../../../../api/NodePromptApiCall";

interface ChatItemProps {
  showTitle?: boolean;
  chat: Prompt;
  onDefaultClicked: (prompt_id: number, isDefault: boolean) => void;
  onFavClicked: (prompt_id: number, isFav: boolean) => void;
  setAction: (isAction: boolean) => void;
}

const ChatItem: React.FC<ChatItemProps> = ({
  showTitle = false,
  chat,
  setAction,
  onDefaultClicked,
  onFavClicked,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateNewDialogConfirm, setShowCreateNewDialogConfirm] =
    useState(false);
  const [promptValue, setPromptValue] = useState(chat.prompt);

  const applyApicall = async () => {
    try {
      const response = await PromptPatch(chat.id, undefined, undefined, true);
      showConsoleMessage("Prompt Patch Response:", response);
      setAction(true);
    } catch (error) {
      showConsoleError("Failed to patch data:", error);
    }
  };
  const handleApply = () => {
    applyApicall();
  };

  const handleEdit = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleUpdatePrompt = () => {
    setShowCreateNewDialogConfirm(true);
  };

  const handleCreatePrompt = async (
    newNodeName: string,
    newPromptValue: string
  ) => {
    try {
      const response = await PostPrompt(newNodeName, newPromptValue);
      setAction(true);
      showConsoleMessage("PostPrompt Response:", response);
    } catch (error) {
      showConsoleError("Failed to PostPrompt data:", error);
    }
  };

  const handleCreateNewConfirmDialogButton = async () => {
    setShowCreateNewDialogConfirm(false);
    handleCreatePrompt(chat.node.node_name, promptValue);
    handleEdit();
  };

  const handleSetFav = async (prompt_id: number, set: boolean) => {
    try {
      const response = await PromptPatch(
        prompt_id,
        undefined,
        undefined,
        undefined,
        undefined,
        set ? FAV_TAG : null
      );
      onFavClicked(prompt_id, set);
      showConsoleMessage("handleSetFav Response:", response);
    } catch (error) {
      showConsoleError("Failed to handleSetFav data:", error);
    }
  };

  const handleSetDefault = async (prompt_id: number, is_default: boolean) => {
    try {
      const response = await PromptPatch(
        prompt_id,
        undefined,
        undefined,
        undefined,
        is_default
      );
      onDefaultClicked(prompt_id, is_default);
      showConsoleMessage("handleSetDefault Response:", response);
    } catch (error) {
      showConsoleError("Failed to handleSetDefault data:", error);
    }
  };

  return (
    <>
      <div className={styles.container}>
        {showTitle && (
          <div>
            <strong>{`Node Name: ${
              NODE_DISPLAY_NAMES[chat.node.node_name]
            }`}</strong>
          </div>
        )}
        <div>{`Date: ${utcToLocalDate(chat.last_updated)} ${utcToLocalTime(
          chat.last_updated
        )}`}</div>
        <div className={styles.card}>
          <div className={clsx(styles.info, styles.codeBlock)}>
            {expanded ? promptValue : `${promptValue.slice(0, 1000)}...`}
          </div>
          <div className={styles.actions}>
            <button
              className={clsx(styles.button, styles.apply)}
              onClick={handleApply}
            >
              Apply
            </button>
            <button
              className={clsx(styles.button, styles.edit)}
              onClick={handleEdit}
            >
              Edit
            </button>
            <button
              className={clsx(styles.button, styles.expand)}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {" "}
              {expanded ? "Collapse" : "Expand"}
            </button>
            <ToggleButton
              data-tooltip={
                chat.tag == FAV_TAG
                  ? "Remove from Favourite"
                  : "Add to Favourite"
              }
              className={clsx(styles.icon_button, styles.fav)}
              isOn={chat.tag == FAV_TAG}
              onValueChange={(value) => {
                handleSetFav(chat.id, value);
              }}
            />
            <ToggleButton
              data-tooltip={
                chat.is_default ? "Default Prompt" : "Make this Default"
              }
              isOn={chat.is_default}
              onValueChange={(value) => {
                handleSetDefault(chat.id, value);
              }}
              className={clsx(styles.icon_button, styles.default)}
              icon="bookmark"
            />
          </div>
        </div>
      </div>
      <PromptModal
        isOpen={isModalOpen}
        onClose={() => {
          setPromptValue(chat.prompt);
          setIsModalOpen(false);
        }}
        onSave={handleUpdatePrompt}
        title={chat.node.node_name}
        instruction={NODE_INSTRUCTIONS[chat.node.node_name]}
      >
        <textarea
          onChange={(e) => setPromptValue(e.target.value)}
          value={promptValue}
          className={styles.input}
          rows={20}
        />
      </PromptModal>

      <ConfirmationDialog
        isOpen={showCreateNewDialogConfirm}
        title="Are you sure you want to update this prompt?"
        onCancel={() => {
          setShowCreateNewDialogConfirm(false);
        }}
        onConfirm={handleCreateNewConfirmDialogButton}
      />
    </>
  );
};

export default ChatItem;
